/**
 * The send-progress rules of the offer detail (TO-466 offer-generation-status
 * BR-6..BR-13, offer-creation-flow §7), kept pure so they are testable without
 * React or timers. The server is authoritative: the view follows `status`,
 * `currentVersion`, `pendingRenderVersion` and `renderFailure`.
 */
import { OfferArtifactStatus, OfferStatus, type RealtimeMessage } from '@today/types'
import {
	OfferArtifactViewState,
	OfferSendAction,
	type OfferTilesView,
	OfferVersionKind,
	type OfferVersionView,
} from '@today/ui-domain'
import type { OfferDTO } from '@/services/offer.service'

/** Refetch cadence while a render is in flight (BR-7). */
export const POLL_INTERVAL_MS = 5_000
/** How long one polling window lasts before the view times out (BR-7/BR-8). */
export const POLL_WINDOW_MS = 180_000

type OfferState = Pick<OfferDTO, 'status' | 'artifacts' | 'currentVersion' | 'pendingRenderVersion' | 'renderFailure'>

export interface GenerationInput {
	/** The last loaded offer, or undefined before the first answer. */
	offer: OfferState | undefined
	/** A realtime failure hint matched the current render, per artefact (BR-9). */
	failed: { landing: boolean; pdf: boolean }
	timedOut: boolean
	canSend: boolean
}

export interface GenerationView {
	/** The in-flight render's tiles; null when nothing renders. */
	tiles: OfferTilesView | null
	shouldPoll: boolean
	showCheckAgain: boolean
	version: OfferVersionView | null
	sendAction: OfferSendAction
}

/** BR-6: a send (SENDING) or a SENT re-version is rendering. */
export const isRenderInFlight = (offer: Pick<OfferState, 'status' | 'pendingRenderVersion'>): boolean =>
	offer.status === OfferStatus.SENDING || offer.pendingRenderVersion !== null

/** A READY artefact always wins (it clears a failure hint and a timeout); a missing one reads as pending. */
const tile = (status: OfferArtifactStatus | undefined, failed: boolean, timedOut: boolean) => {
	if (status === OfferArtifactStatus.READY) return OfferArtifactViewState.READY
	if (failed) return OfferArtifactViewState.FAILED
	return timedOut ? OfferArtifactViewState.TIMED_OUT : OfferArtifactViewState.PENDING
}

/** BR-11: the version line, or null when the needed version is unknown. */
function versionOf({ status, currentVersion, pendingRenderVersion }: OfferState): OfferVersionView | null {
	if (status === OfferStatus.SENDING) {
		return pendingRenderVersion === null ? null : { kind: OfferVersionKind.SENDING, pending: pendingRenderVersion }
	}
	if (status !== OfferStatus.SENT || currentVersion === null) return null
	return pendingRenderVersion === null
		? { kind: OfferVersionKind.LIVE, current: currentVersion }
		: { kind: OfferVersionKind.RENDERING, current: currentVersion, pending: pendingRenderVersion }
}

/** BR-13: Send for a DRAFT, Re-send after a failed send — never gated on readiness (INV-4). */
function sendActionOf(status: OfferStatus | undefined, canSend: boolean): OfferSendAction {
	if (!canSend) return OfferSendAction.NONE
	if (status === OfferStatus.DRAFT) return OfferSendAction.SEND
	if (status === OfferStatus.SEND_FAILED) return OfferSendAction.RESEND
	return OfferSendAction.NONE
}

/** Tiles, polling, version line and send action for the current state. */
export function deriveGeneration({ offer, failed, timedOut, canSend }: GenerationInput): GenerationView {
	const inFlight = offer !== undefined && isRenderInFlight(offer)
	const artifacts = offer?.artifacts
	return {
		tiles: inFlight
			? {
					landing: tile(artifacts?.landing, failed.landing, timedOut),
					pdf: tile(artifacts?.pdf, failed.pdf, timedOut),
				}
			: null,
		shouldPoll: inFlight && !timedOut,
		showCheckAgain: inFlight && timedOut,
		version: offer ? versionOf(offer) : null,
		sendAction: sendActionOf(offer?.status, canSend),
	}
}

/**
 * BR-9: an `offer.landing_failed` / `offer.pdf_failed` hint fails the viewed
 * offer's tile when it names the offer and its current render — or carries no
 * usable `renderId` (read defensively).
 */
export function matchesRenderFailure(message: RealtimeMessage, offerId: string, currentRenderId: string | undefined) {
	if (message.entityRef.id !== offerId) return false
	const renderId = message.data?.renderId
	return typeof renderId !== 'string' || renderId === currentRenderId
}
